import { useState } from 'react';
import { Send, CheckCircle, AlertCircle, Building2, User, Mail, MessageSquare, Phone } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    honeypot: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const res = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setStatus({ type: 'success', message: res.data.message || t('contact.form.success') });
      setFormData({ name: '', company: '', email: '', phone: '', message: '', honeypot: '' });
    } catch (error) {
      const msg = error.response?.status === 429
        ? t('contact.form.rateLimited')
        : t('contact.form.error');
      setStatus({ type: 'error', message: msg });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputFields = [
    { name: 'name', label: t('contact.form.name'), placeholder: t('contact.form.namePlaceholder'), icon: User, type: 'text', required: true },
    { name: 'company', label: t('contact.form.company'), placeholder: t('contact.form.companyPlaceholder'), icon: Building2, type: 'text', required: true },
    { name: 'email', label: t('contact.form.email'), placeholder: t('contact.form.emailPlaceholder'), icon: Mail, type: 'email', required: true },
    { name: 'phone', label: t('contact.form.phone'), placeholder: t('contact.form.phonePlaceholder'), icon: Phone, type: 'tel', required: false },
  ];

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero */}
      <section className="gradient-hero pt-32 md:pt-40 pb-20 md:pb-24">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="text-caption text-[#0077FF] mb-4 block">Contact</span>
            <h1 className="text-h1 text-slate-900 mb-6" data-testid="contact-title">
              {t('contact.hero.title')}
            </h1>
            <p className="text-body-lg">
              {t('contact.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                {inputFields.map((field) => (
                  <div key={field.name}>
                    <label htmlFor={field.name} className="block text-sm font-medium text-slate-700 mb-2">
                      {field.label}
                    </label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0077FF]">
                        <field.icon className="w-5 h-5" />
                      </div>
                      <input
                        type={field.type}
                        id={field.name}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        required={field.required}
                        className="input-default pl-12"
                        data-testid={`contact-input-${field.name}`}
                      />
                    </div>
                  </div>
                ))}

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.form.message')}
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-4 text-[#0077FF]">
                      <MessageSquare className="w-5 h-5" />
                    </div>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('contact.form.messagePlaceholder')}
                      rows={5}
                      required
                      className="input-default pl-12 resize-none"
                      data-testid="contact-input-message"
                    />
                  </div>
                </div>

                {/* Honeypot — hidden from real users */}
                <div className="absolute opacity-0 pointer-events-none" style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                  <input
                    type="text"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {status.message && (
                  <div
                    className={`flex items-center gap-3 p-4 rounded-lg ${
                      status.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                    }`}
                    data-testid="contact-status"
                  >
                    {status.type === 'success' ? (
                      <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    )}
                    <span>{status.message}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center gap-2 w-full bg-[#FF7F00] hover:bg-[#E67200] text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-sm hover:shadow-md active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    <>
                      {t('contact.form.submit')}
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Info Side */}
            <div>
              <div className="sticky top-32">
                {/* Portal Screenshot */}
                <div className="rounded-2xl overflow-hidden shadow-elevated mb-8" data-testid="contact-illustration">
                  <img
                    src="/portal_screenshot.png"
                    alt="Tapelligence Portal — Incident Log & Analytics Dashboard"
                    className="w-full h-auto rounded-2xl"
                  />
                </div>
                <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                  <h3 className="text-h4 text-slate-900 mb-4">
                    Enterprise Inquiries
                  </h3>
                  <p className="text-body mb-4">
                    Our team specializes in helping organizations deploy operational intelligence across complex physical environments.
                  </p>
                  <ul className="space-y-3">
                    {['Multi-location deployments', 'Custom integration requirements', 'Enterprise security needs', 'Pilot programs'].map((item, index) => (
                      <li key={index} className="flex items-center gap-2 text-slate-600 text-sm">
                        <div className="w-1.5 h-1.5 bg-[#0077FF] rounded-full" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
